import { Link } from "react-router";
import type { ProductProfile } from "../../../shared/goals/profile";
import { Icon, type IconName } from "../../components/ui/Icon";
import { answerText, changesLine, edited, walkParts, type WalkState } from "./walk";

const NEXT: [IconName, string, string][] = [
  ["doc", "Playbook", "None yet. Flashadz drafts one from this profile, or you import your notes."],
  ["target", "Goal", "One sentence, a total budget and the most per result."],
  ["tool", "Skills", "Copied from the library for the channels you pick. Special ad rules, like housing on Meta, live here."],
  ["spark", "Plan, review, launch", "The agent plans, you decide, and each part starts when its connections check out."],
];

/** After the owner confirms: what they changed and answered, and the way to the first goal. */
export function Done({ productId, profile, st, version }: { productId: string; profile: ProductProfile; st: WalkState; version: number }) {
  const parts = walkParts(profile);
  const changed = parts.filter((x) => edited(x, st));
  const answered = profile.questions.map((q, k) => [q.question, answerText(profile, st, k)] as const).filter(([, a]) => a);
  return (
    <>
      <section className="card psec">
        <div className="card-h">
          <h3>Your changes and answers</h3>
          <span className="sub">Profile v{version}</span>
        </div>
        <p className="wk-src">{changesLine(st)}</p>
        {changed.length > 0 && (
          <div className="st-meta" style={{ marginTop: 8 }}>
            {changed.map((x) => (
              <span className="chip rl" key={x.id}>
                <Icon name="pen" />
                {x.title}
              </span>
            ))}
          </div>
        )}
        {answered.length > 0 && (
          <div className="rows" style={{ marginTop: 12 }}>
            {answered.map(([q, a], k) => (
              <div className="qarow" key={k}>
                <p>{q}</p>
                <b>{a}</b>
              </div>
            ))}
          </div>
        )}
      </section>
      <section className="card psec">
        <div className="psec-h">
          <div>
            <h3>Next: the first goal</h3>
          </div>
        </div>
        <div className="rows">
          {NEXT.map(([ic, t, d]) => (
            <div className="nxt" key={t}>
              <span className="ki">
                <Icon name={ic} />
              </span>
              <div>
                <b>{t}</b>
                <p>{d}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="m-act" style={{ marginTop: 14 }}>
          <Link className="btn" to={`/products/${encodeURIComponent(productId)}/goals/new`}>
            Start the first goal
            <Icon name="right" />
          </Link>
        </div>
      </section>
    </>
  );
}

/** The same, on a phone or an iPad: what the owner changed and answered, then what comes next, as rows. */
export function DoneNative({ profile, st, version }: { profile: ProductProfile; st: WalkState; version: number }) {
  const parts = walkParts(profile);
  const changed = parts.filter((x) => edited(x, st));
  const answered = profile.questions.map((q, k) => [q.question, answerText(profile, st, k)] as const).filter(([, a]) => a);
  return (
    <>
      <section>
        <div className="pn-cap">Your changes and answers, in profile v{version}</div>
        <div className="pn-grp">
          <div className="pn-row static wrap">
            <span className="m">
              <span className="t">{changesLine(st)}</span>
              {changed.length > 0 && <span className="s">{changed.map((x) => x.title).join(", ")}</span>}
            </span>
          </div>
          {answered.map(([q, a], k) => (
            <div className="pn-row static wrap" key={k}>
              <span className="m">
                <span className="s">{q}</span>
                <span className="t">{a}</span>
              </span>
            </div>
          ))}
        </div>
      </section>
      <section>
        <div className="pn-sh">
          <h2>Next: the first goal</h2>
        </div>
        <div className="pn-grp">
          {NEXT.map(([ic, t, d]) => (
            <div className="pn-row static wrap" key={t}>
              <span className="pn-ico">
                <Icon name={ic} />
              </span>
              <span className="m">
                <span className="t">{t}</span>
                <span className="s">{d}</span>
              </span>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}
